import React, { useEffect, useState, useRef, forwardRef } from "react";
import { useMergeRefs } from "use-callback-ref";
import { hasWindow, noop } from "@litres/utils";
import InputMask from "react-input-mask";
import cn from "classnames";
import type { IInput } from "./Input.types";
import { EyeOpen, EyeClosed } from "./icons/icons";

import styles from "./Input.module.scss";

const selectionRangeSupportedTypes = [
  "text",
  "search",
  "url",
  "tel",
  "password",
];

// eslint-disable-next-line react/display-name
export const Input = forwardRef<HTMLInputElement, IInput>((props, ref) => {
  const inputType = props.type || "text";
  const fieldID =
    props.id ||
    props.name ||
    `input_${String(Math.floor(Math.random() * 9999))}`;
  const [type, setType] = useState(inputType);
  const input = useRef<HTMLInputElement>(null);
  const innerRef = useMergeRefs([input, ref]);

  useEffect(() => {
    if (input && input.current) {
      const length = input.current.value.length;
      if (props.autoFocus) {
        input.current.focus();
      }
      if (selectionRangeSupportedTypes.includes(type)) {
        input.current.setSelectionRange(length, length);
      }
    }
  }, [input, type]);

  const classes = cn(styles.input, props.className, {
    [styles.error]: props.error,
    [styles.input_icon]: props.eye || (props.customIcon && type !== "password"),
  });

  const { onChange = noop } = props;
  const { onBlur = noop } = props;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    onChange(event);
  };

  const changeTypeForPwd = (): void => {
    if (type === "password") {
      setType("text");
    } else {
      setType("password");
    }
  };

  return (
    <div className={cn(styles.input_field, props.rootClassName)}>
      {props.label && (
        <label htmlFor={props.name || fieldID}>{props.label}</label>
      )}
      <div className={styles.input_wrapper}>
        {props.mask ? (
          <InputMask
            type={type}
            disabled={props.disabled}
            name={props.name || fieldID}
            className={classes}
            value={props.value}
            onChange={handleChange}
            onBlur={props.onBlur}
            mask={props.mask}
            maskChar={props.maskChar}
            alwaysShowMask={props.alwaysShowMask}
            autoComplete={props.autoComplete}
            max={props.max || undefined}
            maxLength={props.maxLength || undefined}
            autoFocus={props.autoFocus}
            inputMode={props.inputMode}
            aria-label={props["aria-label"]}
            // @ts-ignore игнорируем ts, так как у нас установлена библиотека с типами для 3ей версии библиотеки, а используем мы 2ую. См. подробнее в https://github.com/sanniassin/react-input-mask/tree/v2?tab=readme-ov-file#beforemaskedvaluechange--function.
            beforeMaskedValueChange={props.beforeMaskedValueChange}
          />
        ) : (
          <input
            ref={innerRef}
            type={type}
            id={props.id || fieldID}
            name={props.name || fieldID}
            value={props.value}
            onChange={handleChange}
            onBlur={onBlur}
            placeholder={props.placeholder}
            className={classes}
            autoComplete={props.autoComplete}
            max={props.max || undefined}
            maxLength={props.maxLength || undefined}
            autoFocus={props.autoFocus}
            disabled={props.disabled}
            data-testid={props.e2eId}
          />
        )}
        {props.eye && (
          <div
            className={cn(styles.icon, styles.pass_eye)}
            onClick={changeTypeForPwd}
          >
            {type === "text" ? <EyeOpen /> : <EyeClosed />}
          </div>
        )}
        {props.customIcon && type !== "password" && (
          <div className={styles.icon}>{props.customIcon}</div>
        )}
      </div>
      {props.errorMsg && (
        <div className={styles.error_text}>{props.errorMsg}</div>
      )}
      {hasWindow && props.hint && !props.errorMsg && (
        <div className={styles.hint}>{props.hint}</div>
      )}
    </div>
  );
});
